import { DeviceTypeEnum } from '../../data/enums'
import { SearchItem } from './useGlobalSearchData'

export const normalizeString = (str: string | null | undefined): string => {
    if (!str) return ''
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Client-side refinement layer for server-side search results
 * Applies intelligent ranking, device type filtering, and exact match prioritization
 * @param searchData - Array of search items from server (already filtered to ~50-150 results)
 * @param searchTerm - The original search term to refine by
 * @returns Refined and ranked array of search items
 */
export function refineSearchResults(searchData: SearchItem[], searchTerm: string): SearchItem[] {
    if (!searchTerm || !searchTerm.trim()) return []

    const normalizedSearchTerm = normalizeString(searchTerm.trim().toLowerCase())
    const terms = normalizedSearchTerm.split(' ').filter(Boolean)
    const firstTermUpper = terms[0].toUpperCase()

    // 1. Handle Device Type Prefix (e.g., "TE 123" or "PD scanner")
    if (Object.values(DeviceTypeEnum).includes(firstTermUpper as DeviceTypeEnum)) {
        const restOfTerm = terms.slice(1).join(' ')
        const deviceTypeMatches: SearchItem[] = []

        for (const item of searchData) {
            if (item.type === 'device' && item.deviceType === firstTermUpper) {
                if (
                    !restOfTerm ||
                    normalizeString(item.name).toLowerCase().includes(restOfTerm) ||
                    normalizeString(item.searchableDescription).toLowerCase().includes(restOfTerm)
                ) {
                    deviceTypeMatches.push(item)
                }
            }
        }
        deviceTypeMatches.sort((a, b) => a.name.localeCompare(b.name))
        return deviceTypeMatches
    }

    const results: SearchItem[] = []

    // 2. Prioritize exact matches on ID or name
    const exactMatch = searchData.find((item) => {
        const normalizedId = normalizeString(item.id).toLowerCase()
        const normalizedName = normalizeString(item.name).toLowerCase()
        return normalizedId === normalizedSearchTerm || normalizedName === normalizedSearchTerm
    })

    if (exactMatch) {
        results.push(exactMatch)
    }

    // 3. Multi-term AND search: All terms must match (in name OR description)
    // Separate name matches from description-only matches for prioritization
    const nameMatches: SearchItem[] = []
    const descriptionOnlyMatches: SearchItem[] = []

    for (const item of searchData) {
        if (item === exactMatch) continue

        const normalizedName = normalizeString(item.name).toLowerCase()
        const normalizedDescription = normalizeString(item.searchableDescription ?? '').toLowerCase()

        const allTermsMatchName = terms.every((term) => normalizedName.includes(term))
        const allTermsMatch = terms.every(
            (term) => normalizedName.includes(term) || normalizedDescription.includes(term)
        )

        if (allTermsMatchName) {
            nameMatches.push(item)
        } else if (allTermsMatch) {
            descriptionOnlyMatches.push(item)
        }
    }

    // Sort each group independently
    nameMatches.sort((a, b) => a.name.localeCompare(b.name))
    descriptionOnlyMatches.sort((a, b) => a.name.localeCompare(b.name))

    // Combine: exact match first, then name matches, then description matches
    if (exactMatch) {
        return [exactMatch, ...nameMatches, ...descriptionOnlyMatches]
    }

    return [...nameMatches, ...descriptionOnlyMatches]
}
